import { publicConfig } from "@/configs/public-config";
import { TokenService } from "@/providers/tokens/tokens.service";
import {
  parseApiError,
  readErrorResponseBody,
  getFilenameFromContentDisposition as getFilenameFromContentDispositionShared,
  buildFetchError,
} from "@/shared/helpers/api-file-download.helper";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

import type {
  IExportMyDistrictSchoolsArg,
  IExportSchoolBoundariesZipArg,
} from "./schools.interface";

export interface IFetchSchoolBoundariesZipResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

export interface IFetchMyDistrictSchoolsResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

const SCHOOL_BOUNDARIES_ZIP_FALLBACK = "school_boundaries.zip";
const MY_DISTRICT_SCHOOLS_FALLBACK = "school_list_export.csv";

export async function fetchMyDistrictSchoolsCsv(
  arg: IExportMyDistrictSchoolsArg,
): Promise<IFetchMyDistrictSchoolsResult> {
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const url = `${baseUrl}/schools/${arg.districtId}/export`;
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};

  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = response.headers.get("Content-Type") ?? "text/csv;charset=utf-8";
    const blob = new Blob([arrayBuffer], { type: contentType });
    const filename = getFilenameFromContentDispositionShared(
      response.headers.get("Content-Disposition"),
      MY_DISTRICT_SCHOOLS_FALLBACK,
    );

    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}

export async function fetchSchoolBoundariesZip(
  arg: IExportSchoolBoundariesZipArg,
): Promise<IFetchSchoolBoundariesZipResult> {
  const { districtId, schoolLevelType } = arg;
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const params = new URLSearchParams({ schoolLevelType });
  const url = `${baseUrl}/schools/${districtId}/boundaries/export/zip?${params.toString()}`;
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};

  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = response.headers.get("Content-Type") ?? "application/zip";
    const blob = new Blob([arrayBuffer], { type: contentType });
    const filename = getFilenameFromContentDispositionShared(
      response.headers.get("Content-Disposition"),
      SCHOOL_BOUNDARIES_ZIP_FALLBACK,
    );

    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
